
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { authRateLimiter, formatTimeUntilReset } from '@/utils/rateLimiter';
import { isValidEmail, sanitizeText } from '@/utils/validation';

export interface UseSignInReturn {
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  isLoading: boolean;
}

export const useSignIn = (): UseSignInReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      console.log("Attempting sign in for:", email);
      setIsLoading(true);
      
      if (!email || !password) {
        toast({
          title: 'Missing credentials',
          description: 'Please provide both email and password',
          variant: 'destructive',
        });
        return { success: false, error: 'Missing credentials' };
      }

      if (!isValidEmail(email)) {
        toast({
          title: 'Invalid email',
          description: 'Please provide a valid email address',
          variant: 'destructive',
        });
        return { success: false, error: 'Invalid email' };
      }

      const sanitizedEmail = sanitizeText(email);
      
      const rateLimitKey = `signin:${sanitizedEmail.toLowerCase()}`;
      const rateLimitResult = authRateLimiter.check(rateLimitKey);
      
      if (!rateLimitResult.allowed) {
        const timeUntilReset = formatTimeUntilReset(rateLimitResult.resetAt || 0);
        toast({
          title: 'Too many sign-in attempts',
          description: `Please try again after ${timeUntilReset}`,
          variant: 'destructive',
        });
        return { success: false, error: `Too many sign-in attempts. Please try again after ${timeUntilReset}` };
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error("Sign in error:", error);
        
        if (error.message.includes('Invalid login credentials')) {
          toast({
            title: 'Authentication failed',
            description: 'Invalid email or password. Please try again.',
            variant: 'destructive',
          });
          return { success: false, error: 'Invalid email or password' };
        } else if (error.message.includes('Email not confirmed')) {
          toast({
            title: 'Email not verified',
            description: 'Please check your email to verify your account before logging in.',
            variant: 'destructive',
          });
          return { success: false, error: 'Email not verified' };
        } else {
          toast({
            title: 'Error signing in',
            description: error.message,
            variant: 'destructive',
          });
          return { success: false, error: error.message };
        }
      }

      console.log("Sign in successful:", data.user?.id);
      authRateLimiter.reset(rateLimitKey);
      
      toast({
        title: 'Welcome back!',
        description: 'You have successfully signed in.',
      });
      
      // Let the Login component handle navigation
      return { success: true };

    } catch (error: any) {
      console.error('Unexpected error during sign in:', error);
      toast({
        title: 'Sign in failed',
        description: 'An unexpected error occurred. Please try again later.',
        variant: 'destructive',
      });
      return { success: false, error: 'An unexpected error occurred' };
    } finally {
      setIsLoading(false);
    }
  }, [toast, navigate]);

  return {
    signIn,
    isLoading
  };
};
